import {
  SET_PROGRAM_MODULES_TREE,
  SET_PROGRAM_MODULES_LOADING,
  SET_PROGRAM_MODULES_CID,
} from './types';
import { BACKEND_URL_PREFIX } from '../utils/const';
import { buildDefaultHeaders } from './helper';
import { checkAndUpdateToken } from './auth';
import { createNotification } from '../components/notifications/notifications';

const BASE_PATH = '/rpd/subjects';

/**
 * Получить дерево разделов и тем дисциплины РПД
 */
export const getTree = (cid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  dispatch({ type: SET_PROGRAM_MODULES_LOADING, payload: true });

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}`,
      buildDefaultHeaders(getState)
    );

    if (response.ok) {
      const data = await response.json();
      dispatch({ type: SET_PROGRAM_MODULES_CID, payload: data.cid });
      dispatch({ type: SET_PROGRAM_MODULES_TREE, payload: data.tree || [] });
    } else {
      dispatch({ type: SET_PROGRAM_MODULES_TREE, payload: [] });
    }
  } catch (error) {
    console.error('getTree error:', error);
    dispatch({ type: SET_PROGRAM_MODULES_TREE, payload: [] });
  } finally {
    dispatch({ type: SET_PROGRAM_MODULES_LOADING, payload: false });
  }
};

/**
 * Сбросить перечень (дисциплина не выбрана) — без обращения к серверу
 */
export const clearTree = () => (dispatch) => {
  dispatch({ type: SET_PROGRAM_MODULES_CID, payload: null });
  dispatch({ type: SET_PROGRAM_MODULES_TREE, payload: [] });
};

/**
 * Добавить раздел/тему на том же уровне после выбранного
 * @param {number} oid - ID элемента (0 = вставить в начало)
 * @param {Object} data - { title, title_index?, description? }
 */
export const createSibling = (oid, data) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      createNotification('success', 'Элемент создан');
      await dispatch(getTree(cid));
      return await response.json();
    } else {
      createNotification('error', 'Ошибка создания элемента');
    }
  } catch (error) {
    createNotification('error', 'Ошибка создания элемента');
  }
};

/**
 * Добавить дочерний раздел/тему уровнем ниже
 * @param {number} oid - ID родительского элемента
 * @param {Object} data - { title, title_index?, description? }
 */
export const createChild = (oid, data) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}/child`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      createNotification('success', 'Элемент создан');
      await dispatch(getTree(cid));
      return await response.json();
    } else {
      createNotification('error', 'Ошибка создания элемента');
    }
  } catch (error) {
    createNotification('error', 'Ошибка создания элемента');
  }
};

/**
 * Обновить поля раздела/темы
 * @param {number} oid - ID элемента
 * @param {Object} data - { title?, title_index?, description? }
 */
export const updateItem = (oid, data) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      createNotification('success', 'Сохранено');
      await dispatch(getTree(cid));
      return await response.json();
    } else {
      createNotification('error', 'Ошибка обновления');
    }
  } catch (error) {
    createNotification('error', 'Ошибка обновления');
  }
};

/**
 * Удалить раздел/тему вместе с поддеревом
 * @param {number} oid - ID элемента
 */
export const deleteItem = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}`,
      {
        method: 'DELETE',
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.success) {
        createNotification('success', 'Элемент удалён');
        await dispatch(getTree(cid));
      } else {
        const message = result.reason === 'check_failed'
          ? 'Невозможно удалить: есть связанные данные'
          : 'Ошибка удаления';
        createNotification('error', message);
      }
      return result;
    } else {
      createNotification('error', 'Ошибка удаления');
    }
  } catch (error) {
    createNotification('error', 'Ошибка удаления');
  }
};

/**
 * Переместить раздел/тему вверх в рамках одного уровня
 * @param {number} oid - ID элемента
 */
export const moveUp = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}/move-up`,
      {
        method: 'PATCH',
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.moved !== false) {
        await dispatch(getTree(cid));
      }
      return result;
    } else {
      createNotification('error', 'Ошибка перемещения');
    }
  } catch (error) {
    createNotification('error', 'Ошибка перемещения');
  }
};

/**
 * Переместить раздел/тему вниз в рамках одного уровня
 * @param {number} oid - ID элемента
 */
export const moveDown = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}/move-down`,
      {
        method: 'PATCH',
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.moved !== false) {
        await dispatch(getTree(cid));
      }
      return result;
    } else {
      createNotification('error', 'Ошибка перемещения');
    }
  } catch (error) {
    createNotification('error', 'Ошибка перемещения');
  }
};

/**
 * Переместить раздел/тему на уровень выше
 * @param {number} oid - ID элемента
 */
export const levelUp = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}/level-up`,
      {
        method: 'PATCH',
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.success) {
        await dispatch(getTree(cid));
      } else if (result.reason === 'already_at_top') {
        createNotification('warning', 'Элемент уже на верхнем уровне');
      }
      return result;
    } else {
      createNotification('error', 'Ошибка перемещения');
    }
  } catch (error) {
    createNotification('error', 'Ошибка перемещения');
  }
};

/**
 * Переместить раздел/тему на уровень ниже
 * @param {number} oid - ID элемента
 */
export const levelDown = (oid) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/${oid}/level-down`,
      {
        method: 'PATCH',
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.success) {
        await dispatch(getTree(cid));
      }
      return result;
    } else {
      createNotification('error', 'Ошибка перемещения');
    }
  } catch (error) {
    createNotification('error', 'Ошибка перемещения');
  }
};

/**
 * Перетащить раздел/тему с поддеревом (drag & drop)
 * @param {number} dragOid - ID перетаскиваемого элемента
 * @param {number} targetOid - ID целевого элемента
 * @param {boolean} levelLower - true = дочерний элемент цели
 */
export const dragAndDrop = (dragOid, targetOid, levelLower = false) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const cid = getState().programModules.cid;
  if (!cid) return;

  try {
    const response = await fetch(
      `${BACKEND_URL_PREFIX}${BASE_PATH}/${cid}/org/drag`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          drag_oid: dragOid,
          target_oid: targetOid,
          level_lower: levelLower,
        }),
        ...buildDefaultHeaders(getState),
      }
    );

    if (response.ok) {
      const result = await response.json();
      await dispatch(getTree(cid));
      return result;
    } else {
      createNotification('error', 'Ошибка перемещения');
    }
  } catch (error) {
    createNotification('error', 'Ошибка перемещения');
  }
};
